import type { PartnerMember } from '../types/partners';

/** A Discord snowflake as the contract carries it: a 17–20 digit string. */
export const isDiscordId = (value: string): boolean => /^\d{17,20}$/.test(value);

/** Pull a Discord ID out of what someone pasted: a bare ID, `<@123…>`, or a profile link. */
export function parseDiscordId(text: string): string | null {
  const trimmed = text.trim();
  if (isDiscordId(trimmed)) return trimmed;
  const mention = /^<@!?(\d{17,20})>$/.exec(trimmed);
  if (mention) return mention[1];
  const profile = /discord(?:app)?\.com\/users\/(\d{17,20})\b/.exec(trimmed);
  if (profile) return profile[1];
  return null;
}

/** A partner's page, optionally opened on one section (and one report). */
export function partnerPagePath(creatorId: string, section?: string | null, reportId?: string | null): string {
  const params = new URLSearchParams();
  if (section) params.set('section', section);
  if (reportId) params.set('report', reportId);
  const qs = params.toString();
  return `/partnerships/partners/${encodeURIComponent(creatorId)}${qs ? `?${qs}` : ''}`;
}

/** Opens the channel in the Discord app or web client. Null until Argus knows the guild. */
export function discordChannelUrl(guildId: string | null | undefined, channelId: string | null | undefined): string | null {
  if (!guildId || !channelId || !isDiscordId(guildId) || !isDiscordId(channelId)) return null;
  return `https://discord.com/channels/${guildId}/${channelId}`;
}

/** The name staff know someone by: the Discord display name, then the username, then the ID. */
export function discordName(
  person: { display_name?: string | null; username?: string | null; discord_display_name?: string | null; discord_username?: string | null },
  fallbackId: string,
): string {
  return (
    person.display_name?.trim() ||
    person.discord_display_name?.trim() ||
    person.username?.trim() ||
    person.discord_username?.trim() ||
    fallbackId
  );
}

export const memberName = (member: PartnerMember): string => discordName(member, member.discord_id);
